/**
 * The words for values the server stores as enums.
 *
 * A role and a ledger entry type are stored in English because that is what
 * the database column holds, and both were reaching the screen that way:
 * a membership chip read «شرکت آزمون — developer» and a wallet row read
 * "capture" in a Persian table. Neither is a status, so neither belongs in
 * StatusChip, and a template literal hides both from the copy audit — which
 * is exactly why they survived the translation pass.
 *
 * An unmapped value falls back to itself rather than to a guess. A role the
 * portal has not been taught is better shown raw than named wrongly, and it
 * is visible enough to get fixed.
 */

const ROLE: Record<string, string> = {
  owner: 'مالک',
  admin: 'مدیر',
  finance: 'مالی',
  developer: 'توسعه‌دهنده',
  viewer: 'بیننده',
}

/**
 * What a ledger line did to the balance.
 *
 * Said as an event rather than as an accounting term: a customer reading
 * their own wallet wants to know money arrived, was spent, or came back.
 */
const LEDGER_ENTRY: Record<string, string> = {
  recharge: 'افزایش اعتبار',
  capture: 'کسر بابت درخواست',
  refund: 'بازگشت وجه',
  adjustment: 'اصلاح دستی',
  external_credit: 'واریز سامانهٔ بیرونی',
  external_debit: 'برداشت سامانهٔ بیرونی',
  external_reverse: 'برگشت عملیات بیرونی',
}

export function roleLabel(role: string | null | undefined): string {
  if (!role) return 'نامشخص'
  return ROLE[role] ?? role
}

export function ledgerEntryLabel(entryType: string | null | undefined): string {
  if (!entryType) return 'نامشخص'
  return LEDGER_ENTRY[entryType] ?? entryType
}

/**
 * Why a request did not succeed, said to the customer whose request it was.
 *
 * The server stores a canonical failure category. Those categories are
 * provider-independent by design — no adapter's own status codes reach this
 * far — but they are named after the boundary they were detected at, and
 * every one of them begins with the word "provider". A customer has no
 * upstream: they called us, and what they need to know is whether to retry,
 * whether they were charged, and whether the answer was a refusal rather than
 * a fault.
 *
 * So these are rewritten around that, not translated word for word:
 *
 *   provider_business_error is an *answer*. The service understood the
 *   request and declined it, which is not the platform failing.
 *
 *   provider_timeout and provider_protocol_error are ambiguous — the work may
 *   have been done — so neither promises the request had no effect.
 *
 *   provider_configuration_error is ours, and saying so is the difference
 *   between a customer retrying forever and a customer contacting us.
 *
 * The stored code stays reachable in the detail view, because it is what a
 * support conversation quotes.
 */
const OUTCOME: Record<string, string> = {
  provider_unavailable: 'سرویس در دسترس نبود. درخواست ارسال نشد و مبلغی کسر نشده است.',
  provider_timeout: 'پاسخی در زمان مقرر دریافت نشد. ممکن است درخواست انجام شده باشد.',
  provider_auth_failed: 'اجرای این درخواست از سمت ما ممکن نشد. با پشتیبانی تماس بگیرید.',
  provider_protocol_error: 'پاسخ دریافتی قابل پردازش نبود. ممکن است درخواست انجام شده باشد.',
  provider_business_error: 'درخواست بررسی و رد شد. این یک پاسخ است، نه خطای سامانه.',
  provider_configuration_error: 'این سرویس هنوز برای اجرا آماده نیست. با پشتیبانی تماس بگیرید.',
  no_attempt: 'هیچ تلاشی برای اجرای این درخواست انجام نشد.',
}

export function outcomeLabel(errorCode: string | null | undefined): string | null {
  if (!errorCode) return null
  return OUTCOME[errorCode] ?? 'این درخواست ناموفق بود.'
}
